import { SiTrackPage } from '../../components/si-track-page';
import { Button, Grid, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import * as Yup from 'yup';
import AddIcon from '@mui/icons-material/Add';
import { WorkOrdersTab } from './workorders-tab/work-orders-tab';
import { SelectChainItem, SelectConfig, SelectResult } from '../../components/select/select-chain.component';
import { customersApiKeyStore } from '../../api/customers-api-key-store';
import { officeApiKeyStore } from '../../api/office-api-key-store';
import { projectsApiKeyStore } from '../../api/projects-api-key-store';
import { useSearchParams } from 'react-router-dom';
import { unitsApiKeyStore } from '../../api/units-api-key-store';
import { workOrdersApiKeyStore } from '../../api/workorders-api-key-store';
import { CreateWorkorderDialog } from './create-workorder/create-workorder-dialog';
import { GuardedComponent } from '../../components/guarded-component';
import { UserPermissionName } from '../../models/user-permission-name';
import { WorkOrderFilters } from './work-order.filters';
import { ReactHookForm } from '../../components/hook-form/react-hook-form.component';
import { useReactHookForm } from '../../hooks/useReactHookForm';
import { gridApiKeyStore } from '../../api/grid-api-key-store';
import { useSaveData } from '../../hooks/useSaveData';
import { useLoadData } from '../../hooks/useLoadData';
import { ApiResponse } from '../../models/api-response';
import { Project } from '../../models/project';
import { GridPreference } from '../../models/gridCoumnConfig';

const styling = {
  paperStyle: { borderRadius: '10px' },
};

const getFiltesFromUrl = (params: URLSearchParams) => {
  return {
    customerID: Number(params.get('customerID')),
    officeID: Number(params.get('officeID')),
    projectID: Number(params.get('projectID')),
    unitID: Number(params.get('unitID')),
    workOrderStatusIDs: params.get('workOrderStatusIDs'),
    workOrderID: Number(params.get('workOrderID')),
    services: params.get('services'),
    serviceGroupID: Number(params.get('serviceGroupID')),
    retestService: params.get('retestService') ?? 'all',
    workOrderActiveStatus: params.get('workOrderActiveStatus') ?? 'all',
    requestedInspections: JSON.parse(params.get('requestedInspections') ?? 'false'),
    searchText: params.get('searchText'),
    pageNumber: params.get('pageNumber') ?? 1,
    pageSize: params.get('pageSize') ?? 50,
  };
};

const validationSchema = Yup.object().shape({
  // customer: Yup.object()
  //   .shape({ customerID: Yup.string().required('Customer is required') })
  //   .nonNullable()
  //   .required('Customer is required'),
});

interface Workorder {
  unitID: number;
  serviceID: number;
}

export const WorkOrdersPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFilters, setSelectedFilters] = useState<SelectResult>(getFiltesFromUrl(searchParams));
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [isWorkOrderRefresh, setIsWorkOrderRefresh] = useState<boolean>(false);
  const [selectedServiceID, setSelectedServiceID] = useState<number>(0);
  const [selectedWorkorderID, setSelectedWorkOrderId] = useState<number>(
    !getFiltesFromUrl(searchParams)?.workOrderID ? 0 : getFiltesFromUrl(searchParams)?.workOrderID
  );

  useEffect(() => {
    if (getFiltesFromUrl(searchParams)?.customerID || getFiltesFromUrl(searchParams)?.officeID ||
      getFiltesFromUrl(searchParams)?.projectID || getFiltesFromUrl(searchParams)?.unitID ||
      getFiltesFromUrl(searchParams)?.requestedInspections || getFiltesFromUrl(searchParams)?.workOrderStatusIDs ||
      getFiltesFromUrl(searchParams)?.services || getFiltesFromUrl(searchParams)?.serviceGroupID) {
      setIsWorkOrderRefresh(true);
    }
  }, [])

  const form = useReactHookForm({
    defaultValues: {
      customer: { customerID: getFiltesFromUrl(searchParams)?.customerID, customerName: '' },
      office: { officeID: getFiltesFromUrl(searchParams)?.officeID, officeName: '' },
      project: { projectID: getFiltesFromUrl(searchParams)?.projectID, projectName: '' },
      unit: { unitID: getFiltesFromUrl(searchParams)?.unitID, unitName: '' },
      jobsite: { unitID: getFiltesFromUrl(searchParams)?.unitID, jobsite: '' },
      requestedInspections: getFiltesFromUrl(searchParams)?.requestedInspections,
      workOrderStatusIDs: getFiltesFromUrl(searchParams)?.workOrderStatusIDs
        ? getFiltesFromUrl(searchParams)!
          .workOrderStatusIDs!.split(',')
          .map((id: string) => ({ workOrderStatusID: Number(id), workOrderStatus: '' }))
        : [],
      service: getFiltesFromUrl(searchParams)?.services
        ? getFiltesFromUrl(searchParams)!
          .services!.split(',')
          .map((id: string) => ({ serviceID: Number(id), serviceName: '' }))
        : [],
      retestService: {
        value: !getFiltesFromUrl(searchParams)?.retestService ? 'all' : getFiltesFromUrl(searchParams)?.retestService,
      },
      workOrderActiveStatus: {
        statusValue: !getFiltesFromUrl(searchParams)?.workOrderActiveStatus ? 'all' : getFiltesFromUrl(searchParams)?.workOrderActiveStatus,
      },
      serviceGroupName: { serviceGroupID: getFiltesFromUrl(searchParams)?.serviceGroupID, serviceGroupName: '' },
    },
    yupSchema: validationSchema,
  });

  const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
  const {
    isLoading: isLoadingGridLayout,
    data: gridLayouts,
    refetch,
  } = useLoadData<ApiResponse<GridPreference>>(gridApiKeyStore.getGridLayout('WorkOrder', 'WorkOrder'));

  const handleSaveLayout = (pageSize: number) => {
    const data = {
      filterJSON: JSON.stringify({ pageSize: pageSize }),
      columnJSON: '',
      gridName: 'WorkOrder',
      pageName: 'WorkOrder',
    };

    mutateAsync({ data }).then(() => {
      refetch();
    });
  };

  useEffect(() => {
    if (searchParams.get('searchText')) {
      const filterData = gridLayouts?.data?.filterJSON ? JSON.parse(gridLayouts.data.filterJSON || '{}') : null;
      if (searchParams.get('searchText') && !isLoadingGridLayout) {
        setSelectedFilters({
          pageNumber: 1,
          pageSize: 50,
          searchText: searchParams.get('searchText'),
          searchType: searchParams.get('searchType'),
        });
      }
      if (searchParams.get('searchText') && filterData) {
        setSelectedFilters({
          pageNumber: 1,
          pageSize: !filterData ? 50 : filterData?.pageSize || 50,
          searchText: searchParams.get('searchText'),
          searchType: searchParams.get('searchType'),
        });
      }
      handleWorkOrderRefresh(true);
    }
  }, [searchParams.get('searchText'), gridLayouts]);

  useEffect(() => {
    if (getFiltesFromUrl(searchParams)?.workOrderID) {
      setIsWorkOrderRefresh(true);
    }
  }, [getFiltesFromUrl(searchParams)?.workOrderID]);

  const handleAddWorkOrder = () => {
    setIsFormOpen(true);
  };
  const handleDialogClose = () => {
    setIsFormOpen(false);
  };

  const onHandleSetSelectedServiceId = (value: number) => {
    setSelectedServiceID(value);
  };

  const handleWorkOrderRefresh = (value: boolean) => {
    setIsWorkOrderRefresh(value);
  };

  const hanldeChangeWorkOrderID = () => {
    if (selectedWorkorderID) {
      setSelectedWorkOrderId(0);
      searchParams?.delete('workOrderID');
      const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
      window.history?.replaceState(null, '', newUrl);
    }
  };

  const handlePageNumberOrPageSizeChange = (pageNumber: number, pageSize: number) => {
    setSelectedFilters({ ...selectedFilters, pageNumber: pageNumber, pageSize: pageSize });
    handleSaveLayout(pageSize);

    if (selectedFilters) {
      setSearchParams({
        ...(selectedFilters.customerID && { customerID: selectedFilters.customerID }),
        ...(selectedFilters.officeID && { officeID: selectedFilters.officeID }),
        ...(selectedFilters.projectID && { projectID: selectedFilters.projectID }),
        ...(selectedFilters.unitID && { unitID: selectedFilters.unitID }),
        ...(selectedFilters.retestService && { retestService: selectedFilters.retestService }),
        ...(selectedFilters.workOrderActiveStatus && { workOrderActiveStatus: selectedFilters.workOrderActiveStatus }),
        ...(selectedFilters.serviceGroupID && { serviceGroupID: selectedFilters.serviceGroupID }),
        ...(selectedFilters.workOrderStatusIDs?.length && {
          workOrderStatusIDs: selectedFilters.workOrderStatusIDs?.map((p: any) => p).join(','),
        }),
        ...(selectedFilters.services?.length && {
          services: selectedFilters.services?.map((p: any) => p).join(','),
        }),

        pageNumber: pageNumber.toString(),
        pageSize: pageSize.toString(),
      });
    }
    setIsWorkOrderRefresh(true);
  };

  const handleSubmit = (filtersData: any) => {
    const filterData = gridLayouts?.data?.filterJSON ? JSON.parse(gridLayouts.data.filterJSON || '{}') : null;

    const filteringData = {
      customerID: filtersData?.customer?.customerID !== '' ? Number(filtersData?.customer?.customerID) : 0,
      officeID: filtersData?.office?.officeID !== '' ? Number(filtersData?.office?.officeID) : 0,
      projectID: filtersData?.project?.projectID !== '' ? Number(filtersData?.project?.projectID) : 0,
      unitID: filtersData?.unit?.unitID !== '' ? Number(filtersData?.unit?.unitID) : 0,
      workOrderStatusIDs: filtersData?.workOrderStatusIDs?.map((p: any) => p.workOrderStatusID),
      services: filtersData?.service?.map((p: any) => p.serviceID),
      workOrderID: selectedWorkorderID,
      retestService: !filtersData?.retestService?.value ? '' : filtersData?.retestService?.value,
      workOrderActiveStatus: !filtersData?.workOrderActiveStatus?.statusValue ? '' : filtersData?.workOrderActiveStatus?.statusValue,
      requestedInspections: filtersData?.requestedInspections,
      serviceGroupID:
        filtersData?.serviceGroupName?.serviceGroupID !== ''
          ? Number(filtersData?.serviceGroupName?.serviceGroupID)
          : 0,
      pageNumber: 1,
      pageSize: !filterData ? 50 : filterData?.pageSize || 50,
    };
    setSelectedFilters(filteringData);
    // Handle searchParams update
    if (selectedFilters) {
      const newSearchParams: Record<string, string | string[]> = {
        ...(filtersData?.customer?.customerID && { customerID: filtersData?.customer?.customerID.toString() }),
        ...(filtersData?.office?.officeID && { officeID: filtersData?.office?.officeID.toString() }),
        ...(filtersData?.project?.projectID && { projectID: filtersData?.project?.projectID.toString() }),
        ...(filtersData?.unit?.unitID && { unitID: filtersData?.unit?.unitID.toString() }),
        ...(filtersData?.retestService?.value && { retestService: filtersData.retestService?.value }),
        ...(filtersData?.workOrderActiveStatus?.statusValue && { workOrderActiveStatus: filtersData.workOrderActiveStatus?.statusValue }),
        ...(filtersData?.requestedInspections && { requestedInspections: filtersData.requestedInspections }),
        ...(filtersData?.serviceGroupName?.serviceGroupID && {
          serviceGroupID: filtersData?.serviceGroupName?.serviceGroupID.toString(),
        }),

        ...(filtersData?.requestedInspections && {
          requestedInspections: filtersData?.requestedInspections,
        }),

        workOrderID: selectedWorkorderID.toString(),

        pageNumber: 1,
        pageSize: !filterData ? 50 : filterData?.pageSize || 50,
      };

      if (filtersData?.workOrderStatusIDs?.length > 0) {
        newSearchParams['workOrderStatusIDs'] = filtersData.workOrderStatusIDs.map((p: any) => p.workOrderStatusID).join(',');
      }

      // If there are multiple services, join them into a single comma-separated string
      if (filtersData?.service?.length > 0) {
        newSearchParams['services'] = filtersData.service.map((p: any) => p.serviceID).join(',');
      }

      // Update the search params with the new object
      setSearchParams(newSearchParams);
    }
    setIsWorkOrderRefresh(true);
  };

  return (
    <SiTrackPage
      title="Work Orders"
      actionButton={
        <GuardedComponent requiredPermissions={[UserPermissionName.ADD_WORKORDER]}>
          <Button
            size="small"
            variant="contained"
            disableElevation={true}
            startIcon={<AddIcon />}
            onClick={handleAddWorkOrder}
          >
            Add Work Order
          </Button>
        </GuardedComponent>
      }
    >
      <Grid container spacing={2}>
        <Grid item lg={12} sm={12} xl={12} xs={12}>
          <ReactHookForm formName="WORK_ORDER_FILTER_FORM" onSubmit={handleSubmit} formContextProps={form}>
            <WorkOrderFilters hanldeChangeWorkOrderID={hanldeChangeWorkOrderID} />
          </ReactHookForm>
        </Grid>
        <Grid item lg={12} sm={12} xl={12} xs={12}>
          <Paper elevation={0} style={styling.paperStyle}>
            <Grid item>
              <WorkOrdersTab
                selectedFilters={selectedFilters}
                isWorkOrderRefresh={isWorkOrderRefresh}
                handleWorkOrderRefresh={handleWorkOrderRefresh}
                handlePageNumberOrPageSizeChange={handlePageNumberOrPageSizeChange}
                searchParams={searchParams}
              />
              {/* <Tabs value={value} onChange={handleTabsChange}>
              <Tab value="workOrders" label="Work Orders" />
              <Tab value="generalWOs" label="General Work Orders" />
            </Tabs>

            <TabPanel selectedValue={value} value="workOrders">
              <WorkOrdersTab selectedFilters={selectedFilters} />
            </TabPanel>
            <TabPanel selectedValue={value} value="generalWOs">
              <GeneralWorkOrdersTab selectedFilters={selectedFilters} />
            </TabPanel> */}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
      {isFormOpen && (
        <CreateWorkorderDialog
          open={isFormOpen}
          onClose={handleDialogClose}
          handleWorkOrderRefresh={handleWorkOrderRefresh}
          selectedFilters={{
            customerID: form.getValues('customer')?.customerID || 0,
            officeID: form.getValues('office')?.officeID || 0,
            projectID: form.getValues('project')?.projectID || 0,
            unitID: form.getValues('unit')?.unitID || 0,
          }}
          page="workOrder"
        />
      )}
    </SiTrackPage>
  );
};
